import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    Avatar,
    Divider,
    IconButton,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
    Snackbar,
    Link,
    Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ReplyIcon from '@material-ui/icons/Reply';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import SendIcon from '@material-ui/icons/Send';
import WarningIcon from '@material-ui/icons/Warning';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import CallMadeIcon from '@material-ui/icons/CallMade';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format, isToday, isYesterday } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'src/utils/axios';
import baseAxios from 'src/utils/baseAxios';
import { getTasksRequest } from 'src/store/actions';
import { getAuthorizedUser } from 'src/utils/Common';
import { getThreadList, getThreadDetails, sendEmail } from 'src/utils/Email2CaseService';
import { sanitizeEmailHtml } from 'src/utils/sanitizeEmailHtml';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: '100%',
        backgroundColor: '#f5f5f5',
        overflow: 'hidden',
    },
    // Sol Panel - Email Listesi
    leftPanel: {
        width: 340,
        minWidth: 340,
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    // Liste Header
    listHeader: {
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    listTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#333',
    },
    tabChip: {
        fontSize: '12px',
        fontWeight: 600,
        height: 28,
        borderRadius: 14,
        '&:hover': {
            backgroundColor: '#e0e0e0',
        },
    },
    tabChipSelected: {
        backgroundColor: '#0078d4',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#106ebe',
        },
    },
    // Arama
    searchContainer: {
        padding: '8px 12px',
        borderBottom: '1px solid #e0e0e0',
    },
    searchField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            height: 32,
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#c0c0c0',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#0078d4',
                borderWidth: 1,
            },
        },
        '& .MuiInputBase-input': {
            fontSize: '13px',
            padding: '6px 8px',
        },
    },
    // Email Liste
    emailList: {
        flex: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
            width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c0c0c0',
            borderRadius: 3,
        },
    },
    emailItem: {
        padding: '10px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    emailItemSelected: {
        backgroundColor: '#e6f2fb',
        borderLeft: '2px solid #0078d4',
        '&:hover': {
            backgroundColor: '#e6f2fb',
        },
    },
    senderRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 4,
    },
    senderAvatar: {
        width: 32,
        height: 32,
        fontSize: '12px',
        fontWeight: 600,
        marginRight: 10,
        backgroundColor: '#0078d4',
    },
    senderInfo: {
        flex: 1,
        minWidth: 0,
    },
    senderName: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#333',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emailDate: {
        fontSize: '11px',
        color: '#666',
        whiteSpace: 'nowrap',
        marginLeft: 8,
    },
    emailSubject: {
        fontSize: '12px',
        color: '#333',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginBottom: 2,
        paddingLeft: 42,
    },
    emailPreview: {
        fontSize: '11px',
        color: '#888',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingLeft: 42,
    },
    workOrderLink: {
        color: '#0078d4',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    // Sağ Panel - Email İçerik
    rightPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
    },
    // Email Header
    emailHeaderBar: {
        padding: '16px 20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fff',
    },
    subjectLine: {
        fontSize: '18px',
        fontWeight: 400,
        color: '#333',
        marginBottom: 8,
    },
    // Email Content Area
    emailContentArea: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#fff',
        '&::-webkit-scrollbar': {
            width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c0c0c0',
            borderRadius: 4,
        },
    },
    // Uyarı Banner - Outlook Style
    cautionBanner: {
        backgroundColor: '#fff8e5',
        borderBottom: '1px solid #ffd54f',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'flex-start',
    },
    cautionIcon: {
        color: '#c17900',
        marginRight: 10,
        marginTop: 2,
        fontSize: 18,
    },
    cautionContent: {
        flex: 1,
    },
    cautionTitle: {
        fontWeight: 700,
        color: '#c17900',
        fontSize: '12px',
        marginBottom: 2,
    },
    cautionText: {
        color: '#c17900',
        fontSize: '11px',
        lineHeight: 1.4,
    },
    // Mesaj Container
    messageContainer: {
        padding: '20px',
    },
    // Inbound (Gelen) mesaj container
    messageContainerInbound: {
        padding: '16px 20px',
        marginRight: '15%',
        marginLeft: 0,
        backgroundColor: '#f0f0f0',
        borderRadius: '0 12px 12px 12px',
        marginBottom: 8,
        border: '1px solid #e0e0e0',
    },
    // Outbound (Giden) mesaj container  
    messageContainerOutbound: {
        padding: '16px 20px',
        marginLeft: '15%',
        marginRight: 0,
        backgroundColor: '#e3f2fd',
        borderRadius: '12px 0 12px 12px',
        marginBottom: 8,
        border: '1px solid #90caf9',
    },
    // Sender Info Block
    senderBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: 16,
    },
    senderAvatarLarge: {
        width: 40,
        height: 40,
        fontSize: '14px',
        fontWeight: 600,
        marginRight: 12,
        backgroundColor: '#0078d4',
    },
    senderDetails: {
        flex: 1,
    },
    senderNameLarge: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#333',
        display: 'flex',
        alignItems: 'center',
    },
    senderEmailText: {
        fontSize: '12px',
        color: '#666',
        marginTop: 2,
    },
    messageTime: {
        fontSize: '12px',
        color: '#666',
    },
    recipientText: {
        fontSize: '12px',
        color: '#666',
        marginTop: 4,
    },
    // Message Body
    messageBodyArea: {
        fontSize: '14px',
        lineHeight: 1.6,
        color: '#333',
        '& p': {
            margin: '8px 0',
        },
        '& a': {
            color: '#0078d4',
        },
    },
    // AI Suggestion
    aiBox: {
        backgroundColor: '#e8f4fc',
        border: '1px solid #b3d9f2',
        borderRadius: 4,
        padding: '12px 16px',
        marginTop: 16,
    },
    aiLabel: {
        fontWeight: 600,
        color: '#0078d4',
        fontSize: '12px',
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    aiContent: {
        fontSize: '13px',
        color: '#333',
        lineHeight: 1.5,
    },
    aiUseButton: {
        fontSize: '11px',
        padding: '4px 12px',
        textTransform: 'none',
        backgroundColor: '#0078d4',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#106ebe',
        },
    },
    // Reply Section
    replyArea: {
        borderTop: '1px solid #e0e0e0',
        padding: '16px 20px',
        backgroundColor: '#fafafa',
    },
    replyButton: {
        color: '#0078d4',
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 400,
        padding: '6px 12px',
        border: '1px solid #0078d4',
        borderRadius: 2,
        marginBottom: 12,
        '&:hover': {
            backgroundColor: '#f0f7ff',
        },
    },
    replyInput: {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#0078d4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#0078d4',
            },
        },
    },
    // Quill Editor Styles
    quillContainer: {
        '& .ql-container': {
            minHeight: 120,
            maxHeight: 300,
            overflow: 'auto',
            fontSize: '14px',
            fontFamily: 'inherit',
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            backgroundColor: '#fff',
        },
        '& .ql-toolbar': {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            backgroundColor: '#fafafa',
            borderColor: '#e0e0e0',
        },
        '& .ql-container.ql-snow': {
            borderColor: '#e0e0e0',
        },
        '& .ql-editor': {
            minHeight: 100,
        },
        '& .ql-editor.ql-blank::before': {
            fontStyle: 'normal',
            color: '#999',
        },
    },
    sendBtn: {
        backgroundColor: '#0078d4',
        color: '#fff',
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        padding: '6px 16px',
        borderRadius: 2,
        marginTop: 10,
        '&:hover': {
            backgroundColor: '#106ebe',
        },
        '&:disabled': {
            backgroundColor: '#ccc',
        },
    },
    // Empty States
    emptyBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#888',
    },
    emptyIcon: {
        fontSize: 56,
        color: '#ddd',
        marginBottom: 12,
    },
    // Loading
    loadingBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    spinner: {
        color: '#0078d4',
    },
    // Divider between messages
    msgDivider: {
        margin: '24px 0',
        borderColor: '#e0e0e0',
    },
    // Direction badge
    directionBadge: {
        fontSize: '11px',
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 12,
        marginLeft: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    inboundBadge: {
        backgroundColor: '#ffecb3',
        color: '#e65100',
        border: '1px solid #ffb74d',
    },
    outboundBadge: {
        backgroundColor: '#c8e6c9',
        color: '#2e7d32',
        border: '1px solid #81c784',
    },
    // Mesaj yönü göstergesi - sol üst köşe
    directionIndicator: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        fontSize: '12px',
        fontWeight: 600,
    },
    inboundIndicator: {
        color: '#e65100',
        '& svg': {
            marginRight: 4,
        },
    },
    outboundIndicator: {
        color: '#2e7d32',
        justifyContent: 'flex-end',
        '& svg': {
            marginLeft: 4,
        },
    },
    closedBadge: {
        backgroundColor: '#ffebee',
        color: '#c62828',
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 4,
        marginLeft: 8,
    },
    activeBadge: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 4,
        marginLeft: 8,
    },
    closedBanner: {
        backgroundColor: '#fff3e0',
        borderBottom: '1px solid #ffb74d',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    signaturePreview: {
        marginTop: 12,
        padding: '12px 16px',
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        border: '1px solid #e0e0e0',
        fontSize: '13px',
        color: '#666',
    },
    signatureText: {
        marginTop: 4,
        color: '#333',
        fontWeight: 600,
        fontSize: '14px',
    },
    blurredSection: {
        filter: 'blur(4px)',
        pointerEvents: 'none',
        userSelect: 'none',
        transition: 'filter 0.3s ease',
    },
    unblurredSection: {
        filter: 'none',
        pointerEvents: 'auto',
        userSelect: 'auto',
        transition: 'filter 0.3s ease',
    },
    showOtherButton: {
        position: 'sticky',
        top: 33,
        zIndex: 10,
        padding: '12px 16px',
        backgroundColor: '#f5f5f5',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: '#eeeeee',
        },
    },
}));

const OutlookStyleEmailView = ({ consumerId, onClose, getTasksRequest, workOrderId, phoneLogOnly = false, readOnly = false }) => {
    const classes = useStyles();
    const { t } = useTranslation("overview");
    const history = useHistory();
    const SessionUser = getAuthorizedUser();

    const [activeTab, setActiveTab] = useState(phoneLogOnly ? 'phonelog' : 'inbox'); // 'inbox' veya 'phonelog' (phoneLogOnly: MailOrbit modunda yalnızca telefon logları)
    const [searchQuery, setSearchQuery] = useState('');
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingThreads, setLoadingThreads] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    
    // PhoneLog için state'ler
    const [phoneLogs, setPhoneLogs] = useState([]);
    const [selectedPhoneLog, setSelectedPhoneLog] = useState(null);
    const [loadingPhoneLogs, setLoadingPhoneLogs] = useState(false);
    const [phoneLogNote, setPhoneLogNote] = useState('');
    const [updatingNote, setUpdatingNote] = useState(false);
    const [showReplyBox, setShowReplyBox] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [showOtherWorkOrders, setShowOtherWorkOrders] = useState(true);
    // To / CC
    const [replyTo, setReplyTo] = useState([]);
    const [replyCC, setReplyCC] = useState([]);
    const [toInput, setToInput] = useState('');
    const [ccInput, setCcInput] = useState('');

    // Email validasyon yardımcıları
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isEmailDuplicate = (email, field) => {
        const lowerEmail = email.toLowerCase();
        return replyTo.some(e => e.toLowerCase() === lowerEmail) ||
            replyCC.some(e => e.toLowerCase() === lowerEmail);
    };

    const addEmailToField = (rawValue, currentList, setList, setInput) => {
        const val = rawValue.trim().replace(/,$/, '');
        if (!val) return;
        if (!isValidEmail(val)) {
            setSnackbar({ open: true, message: t('lblInvalidEmail') || 'Geçersiz email adresi', severity: 'warning' });
            return;
        }
        if (isEmailDuplicate(val)) {
            setSnackbar({ open: true, message: t('lblDuplicateEmail') || 'Bu email zaten ekli', severity: 'warning' });
            return;
        }
        setList([...currentList, val]);
        setInput('');
    };

    // Reply tıklandığında To/CC alanlarını son outbound mesajdan veya ilk inbound sender'dan doldur
    const populateReplyRecipients = () => {
        // Son outbound mesajı bul
        const lastOutbound = [...messages].reverse().find(m => m.Direction === 'Outbound');
        if (lastOutbound) {
            // Son gönderimden To alıcılarını al (semicolon-separated)
            const toList = lastOutbound.Recipient
                ? lastOutbound.Recipient.split(';').map(e => e.trim()).filter(e => e)
                : [];
            // Son gönderimden CC alıcılarını al
            const ccList = lastOutbound.CcRecipient
                ? lastOutbound.CcRecipient.split(';').map(e => e.trim()).filter(e => e)
                : [];
            setReplyTo(toList.length > 0 ? toList : []);
            setReplyCC(ccList);
        } else {
            // Hiç outbound yoksa ilk inbound sender'ı kullan
            const firstInbound = messages.find(m => m.Direction === 'Inbound');
            if (firstInbound && firstInbound.Sender) {
                setReplyTo([firstInbound.Sender]);
            }
            setReplyCC([]);
        }
    };

    // WorkOrder sayfasına yönlendirme - Yeni sekmede aç
    const handleWorkOrderClick = async (workOrderId, e) => {
        e.stopPropagation();
        if (!workOrderId) return;

        try {
            const response = await axios.get(
                `/GetConsumerByWorkOrderId/${workOrderId}/${SessionUser.ServiceShopId}/${SessionUser.Role}`
            );

            if (response.data && response.data[0]) {
                const consumerData = response.data[0];

                // Her tıklamada önce temizle, sonra yeni değerleri set et
                localStorage.removeItem('fromEmail2Case');
                localStorage.removeItem('email2CaseWorkOrderId');
                localStorage.removeItem('email2CaseConsumerData');

                localStorage.setItem('fromEmail2Case', 'true');
                localStorage.setItem('email2CaseWorkOrderId', workOrderId);
                localStorage.setItem('email2CaseConsumerData', JSON.stringify(consumerData));

                // Yeni sekmede WorkOrder sayfasını aç
                window.open(`/WorkOrder/${workOrderId}/summary`, '_blank');
            } else {
                setSnackbar({
                    open: true,
                    message: t('Fis_Bulunamadi')?.replace('@FisNo', workOrderId) || `Fiş bulunamadı: ${workOrderId}`,
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('WorkOrder yönlendirme hatası:', error);
            setSnackbar({
                open: true,
                message: t('Fis_Bulunamadi')?.replace('@FisNo', workOrderId) || `Fiş bulunamadı: ${workOrderId}`,
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        if (consumerId) {
            if (!phoneLogOnly) fetchThreadList();
            fetchPhoneLogs();
        }
    }, [consumerId]);

    useEffect(() => {
        if (consumerId && activeTab === 'phonelog') {
            fetchPhoneLogs();
        }
    }, [activeTab]);

    useEffect(() => {
        if (selectedPhoneLog) {
            setPhoneLogNote(selectedPhoneLog.Notes || '');
        }
    }, [selectedPhoneLog]);

    // PhoneLog verilerini API'den yükle
    const fetchPhoneLogs = async () => {
        setLoadingPhoneLogs(true);
        try {
            const response = await baseAxios.get(`OmniChannel/SearchPhoneLog/${consumerId}`);
            
            if (response.data && Array.isArray(response.data)) {
                // API'den gelen veriyi UI formatına dönüştür
                const formattedLogs = response.data.map((log) => ({
                    Id: log.Id,
                    PhoneNumber: log.CallingPhone || t('lblUnknown') || 'Unknown',
                    CallType: determineCallType(log), // Backend'den gelecek alan yoksa varsayılan
                    CallDate: new Date(log.CreateDate),
                    Duration: calculateDuration(log), // Backend'den gelecek alan yoksa varsayılan
                    WorkOrderId: log.WorkOrderId ? `WO-${log.WorkOrderId}` : t('lblNA') || 'N/A',
                    Notes: log.Note || '', // Backend'den 'Note' alanı geliyor
                    AgentName: log.EmployeeName || t('lblUnknown') || 'Unknown', // Backend'den EmployeeName geliyor
                    Status: log.UpdateDate ? 'Completed' : 'Pending',
                    ContactId: log.ContactId,
                    EmployeeId: log.EmployeeId,
                    PhoneChannelTenantMatchingId: log.PhoneChannelTenantMatchingId
                }));

                // WorkOrderId'ye göre sırala - mevcut workorder önce
                let sortedLogs = formattedLogs;
                if (workOrderId) {
                    const currentWorkOrderLogs = formattedLogs.filter(log => log.WorkOrderId === `WO-${workOrderId}`);
                    const otherLogs = formattedLogs.filter(log => log.WorkOrderId !== `WO-${workOrderId}`);
                    sortedLogs = [...currentWorkOrderLogs, ...otherLogs];
                }

                setPhoneLogs(sortedLogs);
                if (sortedLogs.length > 0) {
                    setSelectedPhoneLog(sortedLogs[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching phone logs:', error);
            setSnackbar({
                open: true,
                message: t('lblFailedToLoadPhoneLogs') || 'Failed to load phone logs',
                severity: 'error'
            });
            setPhoneLogs([]);
        } finally {
            setLoadingPhoneLogs(false);
        }
    };

    // Yardımcı fonksiyonlar - Backend'den bu alanlar gelmediği için varsayılan değerler
    const determineCallType = (log) => {
        // TODO: Backend'den CallType alanı eklendiğinde burası kaldırılacak
        // Şimdilik CreateDate saatine göre rastgele bir tip belirleyelim veya
        // ContactId'nin varlığına göre karar verebiliriz
        const hour = new Date(log.CreateDate).getHours();
        return 'Incoming';
    };

    const calculateDuration = (log) => {
        // TODO: Backend'den Duration alanı eklendiğinde burası kaldırılacak
        // Şimdilik UpdateDate varsa süre hesapla, yoksa 0
        if (log.UpdateDate && log.CreateDate) {
            const start = new Date(log.CreateDate);
            const end = new Date(log.UpdateDate);
            return Math.floor((end - start) / 1000); // saniye cinsinden
        }
        return 0;
    };

    const getAgentName = (employeeId) => {
        // TODO: Employee bilgisi backend'den getirilecek
        // Şimdilik mevcut kullanıcı bilgisini kullan
        if (employeeId === SessionUser?.Id) {
            return `${SessionUser.FirstName || ''} ${SessionUser.LastName || ''}`.trim();
        }
        return `${t('lblAgent') || 'Agent'} #${employeeId}`;
    };

    const fetchThreadList = async () => {
        setLoadingThreads(true);
        try {
            const response = await getThreadList(consumerId);
            if (response.Success) {
                let allThreads = response.Threads || [];

                // Eğer workOrderId prop'u varsa, önce bu workorder'a ait mesajları göster
                if (workOrderId) {
                    const workOrderThreads = allThreads.filter(t => t.WorkOrderId === workOrderId);
                    const otherThreads = allThreads.filter(t => t.WorkOrderId !== workOrderId);
                    allThreads = [...workOrderThreads, ...otherThreads];
                }

                setThreads(allThreads);
                if (allThreads.length > 0) {
                    handleThreadSelect(allThreads[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching thread list:', error);
        } finally {
            setLoadingThreads(false);
        }
    };

    const handleThreadSelect = async (thread) => {
        setSelectedThread(thread);
        setShowReplyBox(false);
        setReplyText('');
        setReplyTo([]);
        setReplyCC([]);
        setToInput('');
        setCcInput('');
        setLoadingMessages(true);
        try {
            const response = await getThreadDetails(
                consumerId,
                thread.ThreadId,
                thread.WorkCaseId,
                thread.WorkOrderId
            );
            if (response.Success) {
                setMessages(response.Messages || []);
            }
        } catch (error) {
            console.error('Error fetching thread details:', error);
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleUpdatePhoneLogNote = async () => {
        if (!selectedPhoneLog) return;
        
        setUpdatingNote(true);
        try {
            const response = await baseAxios.post('OmniChannel/UpdatePhoneLogNote', {
                PhoneLogId: selectedPhoneLog.Id,
                PhoneLogNote: phoneLogNote
            });

            if (response.data) {
                setSnackbar({
                    open: true,
                    message: t('lblNoteUpdatedSuccessfully') || 'Not başarıyla güncellendi',
                    severity: 'success'
                });
                
                // Listeyi güncelle
                setPhoneLogs(prevLogs => 
                    prevLogs.map(log => 
                        log.Id === selectedPhoneLog.Id 
                            ? { ...log, Notes: phoneLogNote }
                            : log
                    )
                );
                
                // SelectedPhoneLog'u güncelle
                setSelectedPhoneLog(prev => ({
                    ...prev,
                    Notes: phoneLogNote
                }));
            }
        } catch (error) {
            console.error('Error updating phone log note:', error);
            setSnackbar({
                open: true,
                message: t('lblFailedToUpdateNote') || 'Not güncellenemedi',
                severity: 'error'
            });
        } finally {
            setUpdatingNote(false);
        }
    };

    const handleSendReply = async () => {
        // ReactQuill returns HTML, check if content is empty (only contains empty tags)
        const strippedContent = replyText.replace(/<[^>]*>/g, '').trim();
        if (!strippedContent || !selectedThread) return;

        setSending(true);
        try {
            // ReactQuill already provides HTML, use it directly
            let emailBody = replyText;

            // ShowSignature true ise kullanıcı imzası ekle
            if (selectedThread.ShowSignature && SessionUser) {
                const fullName = `${SessionUser.FirstName || ''} ${SessionUser.LastName || ''}`.trim();
                if (fullName) {
                    emailBody += `<br/><br/><p style="color: #666; font-size: 0.9em;">--</p><p style="color: #333; font-weight: 600;">${fullName}</p>`;
                }
            }

            const response = await sendEmail(
                consumerId,
                emailBody,
                selectedThread.WorkOrderId,
                selectedThread.WorkCaseId,
                selectedThread.ThreadId,
                replyTo,
                replyCC
            );

            if (response.Success) {
                setSnackbar({
                    open: true,
                    message: t('lblEmailSentSuccessfully'),
                    severity: 'success'
                });
                setReplyText('');

                // Thread mesajlarını yenile ama To/CC'yi koru
                const currentTo = [...replyTo];
                const currentCC = [...replyCC];
                try {
                    const refreshResponse = await getThreadDetails(
                        consumerId,
                        selectedThread.ThreadId,
                        selectedThread.WorkCaseId,
                        selectedThread.WorkOrderId
                    );
                    if (refreshResponse.Success) {
                        setMessages(refreshResponse.Messages || []);
                    }
                } catch (err) {
                    console.error('Error refreshing thread:', err);
                }
                // To/CC'yi geri yükle
                setReplyTo(currentTo);
                setReplyCC(currentCC);
            } else {
                setSnackbar({
                    open: true,
                    message: response.Message || t('lblFailedToSendEmail'),
                    severity: 'error'
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: t('lblFailedToSendEmail'),
                severity: 'error'
            });
        } finally {
            setSending(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isToday(date)) {
                return format(date, 'HH:mm');
            } else if (isYesterday(date)) {
                return t('lblYesterday');
            }
            return format(date, 'MM/dd/yyyy');
        } catch (error) {
            return dateString;
        }
    };

    const formatFullDate = (dateString) => {
        try {
            return format(new Date(dateString), "MMMM d, yyyy EEEE HH:mm", { locale: enUS });
        } catch (error) {
            return dateString;
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getCallTypeIcon = (callType) => {
        switch (callType) {
            case 'Incoming':
                return <CallReceivedIcon style={{ fontSize: 16, color: '#2e7d32' }} />;
            case 'Outgoing':
                return <CallMadeIcon style={{ fontSize: 16, color: '#1976d2' }} />;
            case 'Missed':
                return <WarningIcon style={{ fontSize: 16, color: '#d32f2f' }} />;
            default:
                return null;
        }
    };

    const getCallTypeColor = (callType) => {
        switch (callType) {
            case 'Incoming':
                return '#e8f5e9';
            case 'Outgoing':
                return '#e3f2fd';
            case 'Missed':
                return '#ffebee';
            default:
                return '#f5f5f5';
        }
    };

    const getCallTypeBorderColor = (callType) => {
        switch (callType) {
            case 'Incoming':
                return '#81c784';
            case 'Outgoing':
                return '#64b5f6';
            case 'Missed':
                return '#e57373';
            default:
                return '#e0e0e0';
        }
    };

    const extractName = (emailString) => {
        if (!emailString) return '';
        const match = emailString.match(/^([^<]+)/);
        if (match) return match[1].trim();
        return emailString.split('@')[0];
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const filteredThreads = threads.filter(thread => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            thread.Subject?.toLowerCase().includes(query) ||
            thread.LastSender?.toLowerCase().includes(query)
        );
    });

    // WorkOrder'a göre threadleri ayır
    const currentWorkOrderThreads = workOrderId
        ? filteredThreads.filter(thread => thread.WorkOrderId === workOrderId)
        : [];
    const otherWorkOrderThreads = workOrderId
        ? filteredThreads.filter(thread => thread.WorkOrderId !== workOrderId)
        : filteredThreads;

    return (
        <Box className={classes.root}>
            {/* Left Panel */}
            <Box className={classes.leftPanel}>
                <Box className={classes.listHeader}>
                    {!phoneLogOnly && (
                    <Chip
                        label={t('lblInbox')}
                        onClick={() => setActiveTab('inbox')}
                        className={`${classes.tabChip} ${activeTab === 'inbox' ? classes.tabChipSelected : ''}`}
                        color={activeTab === 'inbox' ? 'primary' : 'default'}
                    />
                    )}
                    <Chip
                        label={t('lblPhoneLog') || 'Phone Log'}
                        onClick={() => setActiveTab('phonelog')}
                        className={`${classes.tabChip} ${activeTab === 'phonelog' ? classes.tabChipSelected : ''}`}
                        color={activeTab === 'phonelog' ? 'primary' : 'default'}
                    />
                </Box>

                {activeTab === 'inbox' ? (
                    <>
                        <Box className={classes.searchContainer}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t('lblSearch')}
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={classes.searchField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 16, color: '#888' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box className={classes.emailList}>
                    {loadingThreads ? (
                        <Box className={classes.loadingBox} style={{ height: 200 }}>
                            <CircularProgress size={28} className={classes.spinner} />
                        </Box>
                    ) : filteredThreads.length === 0 ? (
                        <Box className={classes.emptyBox} style={{ height: 200 }}>
                            <MailOutlineIcon style={{ fontSize: 40, color: '#ddd' }} />
                            <Typography variant="body2" style={{ marginTop: 8, color: '#888' }}>
                                {t('lblNoEmailsFound')}
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {/* Bu WorkOrder'a Ait Mesajlar */}
                            {workOrderId && currentWorkOrderThreads.length > 0 && (
                                <>
                                    <Box style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#fff3e0',
                                        borderBottom: '2px solid #ffa726',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    }}>
                                        <Typography style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: '#e65100',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {t('lblThisWorkOrder') || 'Bu İş Emri'} ({currentWorkOrderThreads.length})
                                        </Typography>
                                    </Box>
                                    {currentWorkOrderThreads.map((thread) => {
                                        const isSelected = selectedThread?.ThreadId === thread.ThreadId;
                                        const subject = thread.Subject || '(No subject)';

                                        return (
                                            <ListItem
                                                key={thread.ThreadId}
                                                className={`${classes.emailItem} ${isSelected ? classes.emailItemSelected : ''}`}
                                                onClick={() => handleThreadSelect(thread)}
                                                disableGutters
                                                style={{ backgroundColor: '#fff9e6', borderLeft: '3px solid #ffa726' }}
                                            >
                                                <Box flex={1}>
                                                    <Box className={classes.senderRow}>
                                                        <Avatar className={classes.senderAvatar}>
                                                            {subject.substring(0, 2).toUpperCase()}
                                                        </Avatar>
                                                        <Box className={classes.senderInfo}>
                                                            <Typography className={classes.senderName}>
                                                                {subject}
                                                            </Typography>
                                                        </Box>
                                                        <Typography className={classes.emailDate}>
                                                            {formatDate(thread.LastMessageDate || thread.FirstMessageDate)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography className={classes.emailSubject}>
                                                        {thread.MessageCount > 1 ? `${thread.MessageCount} ${t('lblMessages')}` : `1 ${t('lblMessage')}`}
                                                        <span className={thread.EmailCaseIsActive ? classes.activeBadge : classes.closedBadge}>
                                                            {thread.EmailCaseIsActive ? t('lblOpen') : t('lblClosed')}
                                                        </span>
                                                    </Typography>
                                                    <Typography className={classes.emailPreview}>
                                                        {t('lblCase')}: {thread.WorkCaseId}
                                                    </Typography>
                                                    <Typography className={classes.emailPreview} style={{ marginTop: 2 }}>
                                                        {t('lblWOStatus')}: {thread.WorkOrderStatus || t('lblNA')}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        );
                                    })}
                                </>
                            )}

                            {/* Diğer WorkOrder'lara Ait Mesajlar - SADECE workOrderId VARSA */}
                            {workOrderId && otherWorkOrderThreads.length > 0 && (
                                <>
                                    <Box
                                        onClick={() => setShowOtherWorkOrders(!showOtherWorkOrders)}
                                        className={classes.showOtherButton}
                                        style={{
                                            borderTop: '1px solid #e0e0e0',
                                            top: currentWorkOrderThreads.length > 0 ? 33 : 0,
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eeeeee'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    >
                                        <Typography style={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#666',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {t('lblOtherWorkOrders') || 'Diğer İş Emirleri'} (${otherWorkOrderThreads.length})
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            style={{
                                                fontSize: '11px',
                                                padding: '4px 12px',
                                                backgroundColor: showOtherWorkOrders ? '#ffffffff' : '#0078d4',
                                                color: '#020202ff',
                                                textTransform: 'none',
                                                minWidth: 'auto'
                                            }}
                                        >
                                            {showOtherWorkOrders ? (t('lblHide') || 'Gizle') : (t('lblShow') || 'Göster')}
                                        </Button>
                                    </Box>
                                    <Box className={showOtherWorkOrders ? classes.unblurredSection : classes.blurredSection}>
                                        {otherWorkOrderThreads.map((thread) => {
                                            const isSelected = selectedThread?.ThreadId === thread.ThreadId;
                                            const subject = thread.Subject || '(No subject)';

                                            return (
                                                <ListItem
                                                    key={thread.ThreadId}
                                                    className={`${classes.emailItem} ${isSelected ? classes.emailItemSelected : ''}`}
                                                    onClick={() => handleThreadSelect(thread)}
                                                    disableGutters
                                                    style={{
                                                        backgroundColor: '#fafafa',
                                                        borderLeft: '3px solid #bdbdbd'
                                                    }}
                                                >
                                                    <Box flex={1}>
                                                        <Box className={classes.senderRow}>
                                                            <Avatar className={classes.senderAvatar} style={{ backgroundColor: '#757575' }}>
                                                                {subject.substring(0, 2).toUpperCase()}
                                                            </Avatar>
                                                            <Box className={classes.senderInfo}>
                                                                <Typography className={classes.senderName}>
                                                                    {subject}
                                                                </Typography>
                                                            </Box>
                                                            <Typography className={classes.emailDate}>
                                                                {formatDate(thread.LastMessageDate || thread.FirstMessageDate)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography className={classes.emailSubject}>
                                                            {thread.MessageCount > 1 ? `${thread.MessageCount} ${t('lblMessages')}` : `1 ${t('lblMessage')}`}
                                                            <span className={thread.EmailCaseIsActive ? classes.activeBadge : classes.closedBadge}>
                                                                {thread.EmailCaseIsActive ? t('lblOpen') : t('lblClosed')}
                                                            </span>
                                                        </Typography>
                                                        <Typography className={classes.emailPreview}>
                                                            <span style={{
                                                                backgroundColor: '#e3f2fd',
                                                                color: '#1565c0',
                                                                padding: '2px 6px',
                                                                borderRadius: 3,
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                marginRight: 6
                                                            }}>
                                                                WO: {thread.WorkOrderId}
                                                            </span>
                                                            {t('lblCase')}: {thread.WorkCaseId}
                                                        </Typography>
                                                        <Typography className={classes.emailPreview} style={{ marginTop: 2 }}>
                                                            {t('lblWOStatus')}: {thread.WorkOrderStatus || t('lblNA')}
                                                        </Typography>
                                                    </Box>
                                                </ListItem>
                                            );
                                        })}
                                    </Box>
                                </>
                            )}

                            {/* Eğer workOrderId yoksa normal liste */}
                            {!workOrderId && filteredThreads.map((thread) => {
                                const isSelected = selectedThread?.ThreadId === thread.ThreadId;
                                const subject = thread.Subject || '(No subject)';

                                return (
                                    <ListItem
                                        key={thread.ThreadId}
                                        className={`${classes.emailItem} ${isSelected ? classes.emailItemSelected : ''}`}
                                        onClick={() => handleThreadSelect(thread)}
                                        disableGutters
                                    >
                                        <Box flex={1}>
                                            <Box className={classes.senderRow}>
                                                <Avatar className={classes.senderAvatar}>
                                                    {subject.substring(0, 2).toUpperCase()}
                                                </Avatar>
                                                <Box className={classes.senderInfo}>
                                                    <Typography className={classes.senderName}>
                                                        {subject}
                                                    </Typography>
                                                </Box>
                                                <Typography className={classes.emailDate}>
                                                    {formatDate(thread.LastMessageDate || thread.FirstMessageDate)}
                                                </Typography>
                                            </Box>
                                            <Typography className={classes.emailSubject}>
                                                {thread.MessageCount > 1 ? `${thread.MessageCount} ${t('lblMessages')}` : `1 ${t('lblMessage')}`}
                                                <span className={thread.EmailCaseIsActive ? classes.activeBadge : classes.closedBadge}>
                                                    {thread.EmailCaseIsActive ? t('lblOpen') : t('lblClosed')}
                                                </span>
                                            </Typography>
                                            <Typography className={classes.emailPreview}>
                                                {t('lblCase')}: {thread.WorkCaseId} • {t('lblWorkOrder')}:{' '}
                                                {onClose ? (
                                                    <span
                                                        className={classes.workOrderLink}
                                                        onClick={(e) => handleWorkOrderClick(thread.WorkOrderId, e)}
                                                        title="Go to WorkOrder"
                                                    >
                                                        {thread.WorkOrderId}
                                                    </span>
                                                ) : (
                                                    <span>{thread.WorkOrderId}</span>
                                                )}
                                            </Typography>
                                            <Typography className={classes.emailPreview} style={{ marginTop: 2 }}>
                                                {t('lblWOStatus')}: {thread.WorkOrderStatus || t('lblNA')}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                        </Box>
                    </>
                ) : (
                    <>
                        <Box className={classes.searchContainer}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t('lblSearchPhoneLog') || 'Search phone logs...'}
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={classes.searchField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 16, color: '#888' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box className={classes.emailList}>
                            {loadingPhoneLogs ? (
                                <Box className={classes.loadingBox} style={{ height: 200 }}>
                                    <CircularProgress size={28} className={classes.spinner} />
                                </Box>
                            ) : phoneLogs.length === 0 ? (
                                <Box className={classes.emptyBox} style={{ height: 200 }}>
                                    <Typography variant="body2" style={{ marginTop: 8, color: '#888' }}>
                                        {t('lblNoPhoneLogsFound') || 'No phone logs found'}
                                    </Typography>
                                </Box>
                            ) : (
                                <List disablePadding>
                                    {phoneLogs.filter(log => {
                                        if (!searchQuery) return true;
                                        const query = searchQuery.toLowerCase();
                                        return (
                                            log.PhoneNumber?.toLowerCase().includes(query) ||
                                            log.Notes?.toLowerCase().includes(query) ||
                                            log.AgentName?.toLowerCase().includes(query)
                                        );
                                    }).map((log) => {
                                        const isSelected = selectedPhoneLog?.Id === log.Id;
                                        const isCurrentWorkOrder = log.WorkOrderId === workOrderId;

                                        return (
                                            <ListItem
                                                key={log.Id}
                                                className={`${classes.emailItem} ${isSelected ? classes.emailItemSelected : ''}`}
                                                onClick={() => setSelectedPhoneLog(log)}
                                                disableGutters
                                                style={{
                                                    backgroundColor: isCurrentWorkOrder ? '#fff9e6' : '#fafafa',
                                                    borderLeft: isCurrentWorkOrder ? '3px solid #ffa726' : '3px solid #bdbdbd'
                                                }}
                                            >
                                                <Box flex={1}>
                                                    <Box className={classes.senderRow}>
                                                        <Avatar className={classes.senderAvatar} style={{
                                                            backgroundColor: log.CallType === 'Incoming' ? '#2e7d32' : 
                                                                            log.CallType === 'Outgoing' ? '#1976d2' : '#d32f2f'
                                                        }}>
                                                            {log.CallType === 'Incoming' ? '📞' : 
                                                             log.CallType === 'Outgoing' ? '📱' : '❌'}
                                                        </Avatar>
                                                        <Box className={classes.senderInfo}>
                                                            <Typography className={classes.senderName}>
                                                                {log.PhoneNumber}
                                                            </Typography>
                                                        </Box>
                                                        <Typography className={classes.emailDate}>
                                                            {formatDate(log.CallDate)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography className={classes.emailSubject}>
                                                        {log.CallType === 'Incoming' ? t('lblIncomingCall') || '📞 Gelen Arama' :
                                                         log.CallType === 'Outgoing' ? t('lblOutgoingCall') || '📱 Giden Arama' :
                                                         t('lblMissedCall') || '❌ Cevapsız Arama'}
                                                        {' • '}
                                                        {formatDuration(log.Duration)}
                                                    </Typography>
                                                    <Typography className={classes.emailPreview}>
                                                        {t('lblAgent') || 'Agent'}: {log.AgentName}
                                                    </Typography>
                                                    <Typography className={classes.emailPreview} style={{ marginTop: 2 }}>
                                                        WO: {log.WorkOrderId}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {/* Right Panel */}
            <Box className={classes.rightPanel}>
                {activeTab === 'inbox' ? (
                    // EMAIL DETAIL PANEL
                    !selectedThread ? (
                    <Box className={classes.emptyBox}>
                        <MailOutlineIcon className={classes.emptyIcon} />
                        <Typography variant="body1" style={{ color: '#666' }}>
                            {t('lblSelectMessageToRead')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box className={classes.emailHeaderBar}>
                            <Typography className={classes.subjectLine}>
                                {selectedThread.Subject || `(${t('lblNoSubject')})`}
                            </Typography>
                            <Box display="flex" alignItems="center" style={{ gap: 16, fontSize: '12px', color: '#666' }}>
                                <span>{t('lblCase')}: {selectedThread.WorkCaseId}</span>
                                <span>
                                    {t('lblWorkOrder')}:{' '}
                                    <span
                                        className={classes.workOrderLink}
                                        onClick={(e) => handleWorkOrderClick(selectedThread.WorkOrderId, e)}
                                        title={t('lblGoToWorkOrder')}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    >
                                        {selectedThread.WorkOrderId}
                                        <OpenInNewIcon style={{ fontSize: 14 }} />
                                    </span>
                                </span>
                            </Box>
                        </Box>

                        <Box className={classes.emailContentArea}>
                            {loadingMessages ? (
                                <Box className={classes.loadingBox}>
                                    <CircularProgress size={32} className={classes.spinner} />
                                    <Typography variant="body2" style={{ marginTop: 12, color: '#888' }}>
                                        {t('lblLoading')}
                                    </Typography>
                                </Box>
                            ) : (
                                messages.map((message, index) => {
                                    const isInbound = message.Direction === 'Inbound';
                                    const senderName = extractName(message.Sender);
                                    const isExternal = isInbound && message.Sender?.includes('@') && !message.Sender?.includes('@arcelik');

                                    return (
                                        <Box key={message.Id || index}>
                                            {/* External email warning */}
                                            {isExternal && index === 0 && (
                                                <Box className={classes.cautionBanner}>
                                                    <WarningIcon className={classes.cautionIcon} />
                                                    <Box className={classes.cautionContent}>
                                                        <Typography className={classes.cautionTitle}>
                                                            {t('lblEmailCautionTitle')}
                                                        </Typography>
                                                        <Typography className={classes.cautionText}>
                                                            {t('lblEmailCautionText')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box style={{ padding: '12px 20px' }}>
                                                {index > 0 && <Divider className={classes.msgDivider} />}

                                                {/* Direction indicator */}
                                                <Box className={`${classes.directionIndicator} ${isInbound ? classes.inboundIndicator : classes.outboundIndicator}`}>
                                                    {isInbound ? (
                                                        <><CallReceivedIcon style={{ fontSize: 16 }} /> {t('lblIncomingMessage')}</>
                                                    ) : (
                                                        <>{t('lblOutgoingMessage')} <CallMadeIcon style={{ fontSize: 16 }} /></>
                                                    )}
                                                </Box>

                                                <Box className={isInbound ? classes.messageContainerInbound : classes.messageContainerOutbound}>
                                                    <Box className={classes.senderBlock}>
                                                        <Avatar className={classes.senderAvatarLarge}>
                                                            {getInitials(senderName)}
                                                        </Avatar>
                                                        <Box className={classes.senderDetails}>
                                                            <Typography className={classes.senderNameLarge}>
                                                                {senderName}
                                                                <span className={`${classes.directionBadge} ${isInbound ? classes.inboundBadge : classes.outboundBadge}`}>
                                                                    {isInbound ? t('lblIncoming') : t('lblOutgoing')}
                                                                </span>
                                                            </Typography>
                                                            <Typography className={classes.senderEmailText}>
                                                                {message.Sender}
                                                            </Typography>
                                                            <Typography className={classes.recipientText}>
                                                                {t('lblTo')}: {message.Recipient ? message.Recipient.split(';').join(', ') : ''}
                                                            </Typography>
                                                            {message.CcRecipient && (
                                                                <Typography className={classes.recipientText}>
                                                                    CC: {message.CcRecipient.split(';').join(', ')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography className={classes.messageTime}>
                                                            {formatFullDate(message.MailDate)}
                                                        </Typography>
                                                    </Box>

                                                    <Box className={classes.messageBodyArea}>
                                                        <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(message.Body) }} />
                                                    </Box>

                                                    {message.LlmRecommendation && (
                                                        <Box className={classes.aiBox}>
                                                            <Box className={classes.aiLabel}>
                                                                <span>💡 {t('lblCopilotSuggestion')}</span>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    className={classes.aiUseButton}
                                                                    onClick={() => {
                                                                        setReplyText(message.LlmRecommendation);
                                                                        populateReplyRecipients();
                                                                        setShowReplyBox(true);
                                                                    }}
                                                                >
                                                                    {t('lblUseThis')}
                                                                </Button>
                                                            </Box>
                                                            <Typography className={classes.aiContent}>
                                                                {message.LlmRecommendation}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>

                        {/* Reply Area */}
                        <Box className={classes.replyArea}>
                            {readOnly ? (
                                <Box className={classes.closedBanner}>
                                    <Typography style={{ color: '#e65100', fontSize: 13 }}>
                                        {t('lblEmail2CaseArchiveReadOnly', { defaultValue: 'Archive: this history is read-only. Reply from the E-mail tab.' })}
                                    </Typography>
                                </Box>
                            ) : !selectedThread.EmailCaseIsActive ? (
                                <Box className={classes.closedBanner}>
                                    <Typography style={{ color: '#e65100', fontSize: 13 }}>
                                        ⚠️ {t('lblThreadClosedCannotReply')}
                                    </Typography>
                                </Box>
                            ) : !showReplyBox ? (
                                <Button
                                    variant="outlined"
                                    startIcon={<ReplyIcon />}
                                    className={classes.replyButton}
                                    onClick={() => {
                                        populateReplyRecipients();
                                        setShowReplyBox(true);
                                    }}
                                >
                                    {t('lblReply')}
                                </Button>
                            ) : (
                                <Box>
                                    <Box className={classes.quillContainer}>
                                        {/* To Alanı */}
                                        <Box style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 4,
                                            padding: '6px 10px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px 4px 0 0',
                                            borderBottom: 'none',
                                            backgroundColor: '#fafafa',
                                            minHeight: 38,
                                        }}>
                                            <Typography style={{ fontSize: 12, color: '#666', minWidth: 24, fontWeight: 600 }}>To:</Typography>
                                            {replyTo.map((email, i) => (
                                                <Chip
                                                    key={i}
                                                    label={email}
                                                    size="small"
                                                    onDelete={() => setReplyTo(replyTo.filter((_, idx) => idx !== i))}
                                                    style={{ fontSize: 11, height: 22 }}
                                                />
                                            ))}
                                            <input
                                                value={toInput}
                                                onChange={(e) => setToInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && toInput.trim()) {
                                                        e.preventDefault();
                                                        addEmailToField(toInput, replyTo, setReplyTo, setToInput);
                                                    } else if (e.key === 'Backspace' && !toInput && replyTo.length > 0) {
                                                        setReplyTo(replyTo.slice(0, -1));
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (toInput.trim()) {
                                                        addEmailToField(toInput, replyTo, setReplyTo, setToInput);
                                                    }
                                                }}
                                                placeholder={replyTo.length === 0 ? t('lblAddRecipient') || 'Alıcı ekle...' : ''}
                                                style={{
                                                    border: 'none',
                                                    outline: 'none',
                                                    fontSize: 12,
                                                    flex: 1,
                                                    minWidth: 120,
                                                    backgroundColor: 'transparent',
                                                }}
                                            />
                                        </Box>
                                        {/* CC Alanı */}
                                        <Box style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 4,
                                            padding: '6px 10px',
                                            border: '1px solid #e0e0e0',
                                            borderBottom: 'none',
                                            backgroundColor: '#fafafa',
                                            minHeight: 38,
                                        }}>
                                            <Typography style={{ fontSize: 12, color: '#666', minWidth: 24, fontWeight: 600 }}>CC:</Typography>
                                            {replyCC.map((email, i) => (
                                                <Chip
                                                    key={i}
                                                    label={email}
                                                    size="small"
                                                    onDelete={() => setReplyCC(replyCC.filter((_, idx) => idx !== i))}
                                                    style={{ fontSize: 11, height: 22 }}
                                                />
                                            ))}
                                            <input
                                                value={ccInput}
                                                onChange={(e) => setCcInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && ccInput.trim()) {
                                                        e.preventDefault();
                                                        addEmailToField(ccInput, replyCC, setReplyCC, setCcInput);
                                                    } else if (e.key === 'Backspace' && !ccInput && replyCC.length > 0) {
                                                        setReplyCC(replyCC.slice(0, -1));
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (ccInput.trim()) {
                                                        addEmailToField(ccInput, replyCC, setReplyCC, setCcInput);
                                                    }
                                                }}
                                                placeholder={replyCC.length === 0 ? t('lblAddCC') || 'CC ekle...' : ''}
                                                style={{
                                                    border: 'none',
                                                    outline: 'none',
                                                    fontSize: 12,
                                                    flex: 1,
                                                    minWidth: 120,
                                                    backgroundColor: 'transparent',
                                                }}
                                            />
                                        </Box>
                                        <ReactQuill
                                            theme="snow"
                                            value={replyText}
                                            onChange={setReplyText}
                                            placeholder={t('lblTypeYourReply')}
                                            readOnly={sending}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                                                    ['link'],
                                                    ['clean']
                                                ],
                                            }}
                                            formats={[
                                                'header',
                                                'bold', 'italic', 'underline', 'strike',
                                                'color', 'background',
                                                'list', 'bullet', 'indent',
                                                'link'
                                            ]}
                                        />
                                    </Box>
                                    {selectedThread?.ShowSignature && SessionUser && (
                                        <Box className={classes.signaturePreview}>
                                            <Typography variant="caption" style={{ color: '#666' }}>--</Typography>
                                            <Typography className={classes.signatureText}>
                                                {`${SessionUser.FirstName || ''} ${SessionUser.LastName || ''}`.trim()}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                                        <Button
                                            onClick={() => {
                                                setShowReplyBox(false);
                                                setReplyText('');
                                                setReplyTo([]);
                                                setReplyCC([]);
                                                setToInput('');
                                                setCcInput('');
                                            }}
                                            style={{ marginRight: 8, textTransform: 'none' }}
                                            disabled={sending}
                                        >
                                            {t('lblCancel')}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            className={classes.sendBtn}
                                            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                                            onClick={handleSendReply}
                                            disabled={sending || !replyText.trim()}
                                        >
                                            {sending ? t('lblSending') : t('lblSend')}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </>
                    )
                ) : (
                    // PHONE LOG DETAIL PANEL
                    !selectedPhoneLog ? (
                        <Box className={classes.emptyBox}>
                            <Typography variant="body1" style={{ color: '#666', fontSize: 48 }}>
                                📞
                            </Typography>
                            <Typography variant="body1" style={{ color: '#666', marginTop: 12 }}>
                                {t('lblSelectPhoneLogToView') || 'Select a phone log to view details'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box className={classes.emailHeaderBar}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {getCallTypeIcon(selectedPhoneLog.CallType)}
                                    <Typography className={classes.subjectLine}>
                                        {selectedPhoneLog.CallType === 'Incoming' ? t('lblIncomingCall') || 'Gelen Arama' :
                                         selectedPhoneLog.CallType === 'Outgoing' ? t('lblOutgoingCall') || 'Giden Arama' :
                                         t('lblMissedCall') || 'Cevapsız Arama'}
                                    </Typography>
                                    <span className={selectedPhoneLog.Status === 'Completed' ? classes.activeBadge : classes.closedBadge}>
                                        {selectedPhoneLog.Status === 'Completed' ? t('lblCompleted') || 'Tamamlandı' : t('lblPending') || 'Bekliyor'}
                                    </span>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: 16, fontSize: '12px', color: '#666', marginTop: 8 }}>
                                    <span>{t('lblPhoneNumber') || 'Telefon'}: {selectedPhoneLog.PhoneNumber}</span>
                                    <span>|</span>
                                    <span>
                                        {t('lblWorkOrder')}:{' '}
                                        <span
                                            className={classes.workOrderLink}
                                            onClick={(e) => handleWorkOrderClick(selectedPhoneLog.WorkOrderId, e)}
                                            title={t('lblGoToWorkOrder')}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                        >
                                            {selectedPhoneLog.WorkOrderId}
                                            <OpenInNewIcon style={{ fontSize: 14 }} />
                                        </span>
                                    </span>
                                </Box>
                            </Box>

                            <Box className={classes.emailContentArea}>
                                <Box style={{ padding: '20px' }}>
                                    {/* Call Details Card */}
                                    <Box style={{
                                        backgroundColor: getCallTypeColor(selectedPhoneLog.CallType),
                                        border: `1px solid ${getCallTypeBorderColor(selectedPhoneLog.CallType)}`,
                                        borderRadius: '8px',
                                        padding: '20px',
                                        marginBottom: '20px'
                                    }}>
                                        <Box display="flex" alignItems="flex-start" gap={2}>
                                            <Avatar style={{
                                                width: 56,
                                                height: 56,
                                                backgroundColor: selectedPhoneLog.CallType === 'Incoming' ? '#2e7d32' :
                                                                 selectedPhoneLog.CallType === 'Outgoing' ? '#1976d2' : '#d32f2f',
                                                fontSize: '28px'
                                            }}>
                                                {selectedPhoneLog.CallType === 'Incoming' ? '📞' :
                                                 selectedPhoneLog.CallType === 'Outgoing' ? '📱' : '❌'}
                                            </Avatar>
                                            <Box flex={1}>
                                                <Typography style={{ fontSize: '18px', fontWeight: 600, marginBottom: 8 }}>
                                                    {selectedPhoneLog.PhoneNumber}
                                                </Typography>
                                                <Box display="flex" flexDirection="column" gap={2}>
                                                    <Box>
                                                        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                                                            {t('lblCallDate') || 'Tarih'}
                                                        </Typography>
                                                        <Typography style={{ fontSize: '14px', fontWeight: 600 }}>
                                                            {formatFullDate(selectedPhoneLog.CallDate)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                                                            {t('lblDuration') || 'Süre'}
                                                        </Typography>
                                                        <Typography style={{ fontSize: '14px', fontWeight: 600 }}>
                                                            {formatDuration(selectedPhoneLog.Duration)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                                                            {t('lblAgent') || 'Temsilci'}
                                                        </Typography>
                                                        <Typography style={{ fontSize: '14px', fontWeight: 600 }}>
                                                            {selectedPhoneLog.AgentName}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Call Notes */}
                                    <Box style={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        padding: '20px'
                                    }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                                            <Typography style={{ 
                                                fontSize: '14px', 
                                                fontWeight: 600,
                                                color: '#000000ff'
                                            }}>
                                                📝 {t('lblCallNotes') || 'Görüşme Notları'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={handleUpdatePhoneLogNote}
                                                disabled={updatingNote || phoneLogNote === (selectedPhoneLog.Notes || '')}
                                                startIcon={updatingNote ? <CircularProgress size={14} color="inherit" /> : null}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '4px 12px',
                                                    textTransform: 'none',
                                                    backgroundColor: '#0078d4',
                                                    minWidth: 'auto'
                                                }}
                                            >
                                                {updatingNote ? t('lblUpdating') || 'Güncelleniyor...' : t('lblUpdate') || 'Güncelle'}
                                            </Button>
                                        </Box>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            value={phoneLogNote}
                                            onChange={(e) => setPhoneLogNote(e.target.value)}
                                            placeholder={t('lblEnterCallNotes') || 'Görüşme notlarını girin...'}
                                            disabled={updatingNote}
                                            style={{
                                                fontSize: '14px'
                                            }}
                                            InputProps={{
                                                style: {
                                                    fontSize: '14px',
                                                    lineHeight: 1.6
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Related Work Order Info */}
                                    <Box style={{
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #90caf9',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginTop: '16px'
                                    }}>
                                        <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#1565c0', marginBottom: 8 }}>
                                            🔗 {t('lblRelatedWorkOrder') || 'İlişkili İş Emri'}
                                        </Typography>
                                        <Typography style={{ fontSize: '13px', color: '#333' }}>
                                            {t('lblWorkOrderId') || 'İş Emri No'}: {' '}
                                            <span
                                                className={classes.workOrderLink}
                                                onClick={(e) => handleWorkOrderClick(selectedPhoneLog.WorkOrderId, e)}
                                                style={{ fontWeight: 600 }}
                                            >
                                                {selectedPhoneLog.WorkOrderId || "N/A"}
                                            </span>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

const mapDispatchToProps = {
    getTasksRequest
};

export default connect(null, mapDispatchToProps)(OutlookStyleEmailView);
